import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import { SnackbarCloseReason } from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RRLink, useLocation, useSearchParams } from 'react-router-dom';

import { checkPracticeNameAvailability } from '../../api/practiceNameAPI';
import { SignInError } from '../../api/sessionAPI';
import { addNewUser } from '../../api/usersAPI';
import {
  confirmSignUp,
  requestPasswordReset,
  requiresMfa,
  respondToMfaChallenge,
  signIn,
  signUp,
  verifyResetCode,
} from '../../authService';
import appConfig from '../../config/appConfig';
import { BrandingProvider, useBrandingIdentity } from '../../context/BrandingContext';
import { validEmail } from '../../utils/emailUtils';
import { isValidPhoneNumber } from '../../utils/phoneNumberUtils';
import { getSubdomainFromUrl } from '../../utils/subdomainUtils';
import { BrandedLogo } from '../common/BrandedLogo/BrandedLogo';

import DocumentViewerModal from './DocumentViewerModal';
import { ResetStep } from './enums/resetStep';
import ErrorSnackbar from './ErrorSnackbar';
import MfaChallengeForm, { MFA_CODE_LENGTH } from './MfaChallengeForm';
import SignUpForm from './SignUpForm';
import SignUpTabs from './SignUpTabs';
import { useSignUpPageStyles } from './styles/signUpPageStyles';
import ConfirmationSnackbar from './SuccessfulConfirm';
import { MIN_PASSWORD_LENGTH } from '../../utils/passwordUtils';

const MIN_EXISTING_PASSWORD_LENGTH = 8;

/**
 * Inner content component that uses branding context for practice-branded login.
 */
const SignUpPageContent: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSignUp, setIsSignUp] = useState(true);
  const { displayName, hasCustomBranding } = useBrandingIdentity();

  useEffect(() => {
    // Route matching is case-insensitive, so /login (where session-expiry redirects go) renders
    // here too, not through LoginRedirect: it is the sign-in page all the same.
    if (location.pathname.toLowerCase() === '/login') {
      setIsSignUp(false);
    } else {
      setIsSignUp(true);
    }
  }, [location.pathname]);

  // HA-208: Handle session expired redirect - show notification and clear URL param
  // HA-241: Handle deliberate sign-out with friendly message
  useEffect(() => {
    const reason = searchParams.get('reason');

    if (reason === 'session_expired') {
      setIsSignUp(false);
      setErrorMessage('Your session has expired. Please sign in again.');
      setOpenErrorSnackbar(true);
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    } else if (reason === 'signed_out') {
      setIsSignUp(false);
      setErrorMessage('You have been signed out successfully.');
      setOpenErrorSnackbar(true);
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [confirmationCode, setConfirmationCode] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isConfirm, setIsConfirm] = useState(false);
  const [addNewUserToSystem, setAddNewUserToSystem] = useState(false);
  const [isForcePasswordChange, setIsForcePasswordChange] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [lastName, setLastName] = useState('');
  const [modalContent, setModalContent] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [practiceError, setPracticeError] = useState('');
  const [practiceName, setPracticeName] = useState('');
  const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState(false);
  const [privacyPolicyClicked, setPrivacyPolicyClicked] = useState(false);
  const [resetStep, setResetStep] = useState(ResetStep.EMAIL);
  const [tabValue, setTabValue] = useState('practiceAdmin');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [termsOfServiceChecked, setTermsOfServiceChecked] = useState(false);
  const [termsOfServiceClicked, setTermsOfServiceClicked] = useState(false);
  // HA-232: Loading state for submit button
  const [isLoading, setIsLoading] = useState(false);
  // HA-440: set while Cognito waits for an authenticator code after the password.
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const [mfaChallengeName, setMfaChallengeName] = useState<'SOFTWARE_TOKEN_MFA' | 'SMS_MFA'>(
    'SOFTWARE_TOKEN_MFA'
  );
  const [mfaCode, setMfaCode] = useState('');

  // Get trial-period from configuration
  const trialPeriodDays = appConfig.trial.periodDays;

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    if (!validEmail(email)) {
      setEmailError('Invalid email');
      setOpenErrorSnackbar(true);

      return;
    }

    // HA-232: Prevent duplicate submissions
    if (isLoading) return;
    setIsLoading(true);

    try {
      await requestPasswordReset(email);
      setResetStep(ResetStep.CODE);
      // Show success message for reset code sent
      setOpenSnackbar(true);
    } catch (error) {
      setErrorMessage(
        (error instanceof Error && error.message) || 'Failed to request password reset'
      );
      setOpenErrorSnackbar(true);
      // Reset the step if there's an error
      setResetStep(ResetStep.EMAIL);
    } finally {
      setIsLoading(false);
    }
  };
  const handleVerifyAndUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // Only check confirmation code for password reset flow, not for force password change
    if (!isForcePasswordChange && (!confirmationCode || confirmationCode.trim() === '')) {
      setErrorMessage('Please enter the confirmation code sent to your email');
      setOpenErrorSnackbar(true);

      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setOpenErrorSnackbar(true);

      return;
    }

    if (!isValidPassword(password, setPasswordError)) {
      setOpenErrorSnackbar(true);

      return;
    }

    // HA-232: Prevent duplicate submissions
    if (isLoading) return;
    setIsLoading(true);

    try {
      if (isForcePasswordChange) {
        await signIn(email, temporaryPassword, password);

        // Show success message first (while isForcePasswordChange is still true)
        setOpenSnackbar(true);

        // Use setTimeout to ensure the snackbar has time to show with the correct type
        // before we reset the state
        setTimeout(() => {
          // Reset state after showing the message
          setIsForcePasswordChange(false);
          setTemporaryPassword('');
          setIsPasswordReset(false);
          setResetStep(ResetStep.NOT_STARTED);

          // Clear password fields
          setPassword('');
          setConfirmPassword('');

          // No delay needed for force password change - user is now logged in
          setIsSignUp(false);
        }, 1000);
      } else {
        await verifyResetCode(email, confirmationCode, password);

        // Show success message and reset the form
        setOpenSnackbar(true);
        setIsPasswordReset(false);
        setResetStep(ResetStep.SUCCESS);

        // Redirect to login after successful password reset with delay
        setTimeout(() => {
          setIsSignUp(false);
          setPassword('');
          setConfirmPassword('');
          setConfirmationCode('');
        }, 2000);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '';

      setErrorMessage(message || 'Failed to verify code or update password');
      setOpenErrorSnackbar(true);

      // If code has expired, go back to email step
      if (message.includes('expired')) {
        setResetStep(ResetStep.EMAIL);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (content: string, title: string) => {
    setModalContent(content);
    setModalTitle(title);
    setIsModalOpen(true);
  };

  const handleAcceptModal = () => {
    if (modalTitle === 'Privacy Policy') {
      setPrivacyPolicyClicked(true);
      setPrivacyPolicyChecked(true);
    } else if (modalTitle === 'Terms of Service') {
      setTermsOfServiceClicked(true);
      setTermsOfServiceChecked(true);
    }
    setIsModalOpen(false);
  };

  const handleDeclineModal = () => {
    setIsModalOpen(false);
    if (modalTitle === 'Privacy Policy') {
      setPrivacyPolicyClicked(false);
      setPrivacyPolicyChecked(false);
    } else if (modalTitle === 'Terms of Service') {
      setTermsOfServiceClicked(false);
      setTermsOfServiceChecked(false);
    }
  };

  const isValidEmail = (email: string) => {
    if (!validEmail(email)) {
      setEmailError('The email provided is not valid.');

      return false;
    }
    setEmailError('');

    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(e.target.value);
    if (e.target.value.length >= 5) {
      setEmailError('');
    }
  };

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLastName(e.target.value);
  };

  const handlePhoneNumberChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPhoneNumber(e.target.value);
    if (isValidPhoneNumber(e.target.value)) {
      setPhoneNumberError('');
    } else {
      setPhoneNumberError(
        'A phone number can only contain the + sign and digits. Remove any other characters such as text or special characters'
      );
    }
  };

  const handleDateOfBirthChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setDateOfBirth(e.target.value);
  };

  // HA-458: new passwords need 12 characters. Signing in doesn't: passwords set under the old
  // rule of 8 still work, and raising this would lock those people out.
  const minPasswordLength =
    isSignUp || isForcePasswordChange || isPasswordReset
      ? MIN_PASSWORD_LENGTH
      : MIN_EXISTING_PASSWORD_LENGTH;
  const passwordTooShort = `Password must be at least ${minPasswordLength} characters long.`;

  const isValidPassword = (
    password: string,
    setError: React.Dispatch<React.SetStateAction<string>>
  ) => {
    if (password.length < minPasswordLength) {
      setError(passwordTooShort);

      return false;
    }
    setError('');

    return true;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPassword(e.target.value);
    if (e.target.value.length >= minPasswordLength) {
      setPasswordError('');

      if (e.target.value === confirmPassword) {
        setConfirmPasswordError('');
        setPasswordError('');
      } else {
        setPasswordsDoNotMatchError();
      }
    } else {
      setPasswordError(passwordTooShort);
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setConfirmPassword(e.target.value);
    if (e.target.value.length >= minPasswordLength) {
      setConfirmPasswordError('');
      if (e.target.value === password) {
        setConfirmPasswordError('');
        setPasswordError('');
      } else if (isSignUp) {
        setPasswordsDoNotMatchError();
      }
    } else {
      setConfirmPasswordError(passwordTooShort);
    }
  };

  const setPasswordsDoNotMatchError = () => {
    if (isSignUp) {
      setPasswordError('Passwords do not match');
      setConfirmPasswordError('Passwords do not match');
    }
  };

  // HA-445: the API set the session cookie; the full page load reads the session (index.tsx).
  const completeSignIn = async () => {
    if (addNewUserToSystem) {
      const newUser = await addNewUser();

      if (newUser) {
        setAddNewUserToSystem(true);
      }
    }

    window.location.href = '/Dashboard';
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!isValidEmail(email) || !isValidPassword(password, setPasswordError)) {
      setOpenErrorSnackbar(true);

      return;
    }

    // HA-232: Prevent duplicate submissions
    if (isLoading) return;
    setIsLoading(true);

    try {
      const step = await signIn(email, password);

      if (step.status === 'challenge' && step.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // For force password change, we don't use the password reset flow states
        setIsForcePasswordChange(true);
        setTemporaryPassword(password);
        // Clear the password field to avoid confusion when entering a new password
        setPassword('');
        // Don't set isPasswordReset or resetStep for force password change
        setIsLoading(false);

        return;
      }

      // HA-440: the account has an authenticator app - ask for its code.
      if (requiresMfa(step)) {
        setMfaChallengeId(step.challengeId);
        setMfaChallengeName('SOFTWARE_TOKEN_MFA');
        setMfaCode('');
        setIsLoading(false);

        return;
      }

      if (step.status === 'signed-in') {
        await completeSignIn();
      } else {
        setErrorMessage('This account needs a sign-in step the app does not support yet.');
        setOpenErrorSnackbar(true);
      }
    } catch (error) {
      const errorMsg = (error instanceof Error && error.message) || String(error);

      setErrorMessage(errorMsg);
      setOpenErrorSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const resetMfaChallenge = () => {
    setMfaChallengeId(null);
    setMfaCode('');
    setPassword('');
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Enter in the code field submits even while the button is disabled.
    if (isLoading || !mfaChallengeId || mfaCode.length !== MFA_CODE_LENGTH) return;
    setErrorMessage('');
    setIsLoading(true);

    try {
      const step = await respondToMfaChallenge(mfaChallengeId, mfaCode);

      if (step.status === 'signed-in') {
        await completeSignIn();

        return;
      }

      resetMfaChallenge();
      setErrorMessage('This account needs a sign-in step the app does not support yet.');
      setOpenErrorSnackbar(true);
    } catch (error) {
      const code = error instanceof SignInError ? error.code : undefined;

      if (code === 'INVALID_CODE') {
        // HA-445: a challenge takes one answer, so sign in again (the password is still here) for a
        // new one and ask for the code again.
        setMfaCode('');
        setErrorMessage('That code could not be verified. Please try again.');
        try {
          const retry = await signIn(email, password);

          if (requiresMfa(retry)) {
            setMfaChallengeId(retry.challengeId);
          } else if (retry.status === 'signed-in') {
            // No code asked this time (MFA turned off meanwhile): the user is signed in.
            await completeSignIn();

            return;
          } else {
            resetMfaChallenge();
          }
        } catch (retryError) {
          resetMfaChallenge();
          setErrorMessage(
            (retryError instanceof Error && retryError.message) || String(retryError)
          );
        }
      } else if (code === 'CHALLENGE_EXPIRED') {
        // The sign-in step expired (3 minutes) - start over from the password.
        resetMfaChallenge();
        setErrorMessage('Your sign-in session expired. Please sign in again.');
      } else {
        // The challenge took its one answer: start again from the password.
        resetMfaChallenge();
        setErrorMessage((error instanceof Error && error.message) || String(error));
      }
      setOpenErrorSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    if (
      !isValidEmail(email) ||
      !isValidPassword(password, setPasswordError) ||
      !isValidPassword(confirmPassword, setConfirmPasswordError)
    ) {
      setOpenErrorSnackbar(true);

      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setOpenErrorSnackbar(true);

      return;
    }

    if (
      !privacyPolicyChecked ||
      !termsOfServiceChecked ||
      !privacyPolicyClicked ||
      !termsOfServiceClicked
    ) {
      setErrorMessage('You must agree to the privacy policy and terms of service.');
      setOpenErrorSnackbar(true);

      return;
    }

    // HA-232: Prevent duplicate submissions
    if (isLoading) return;
    setIsLoading(true);

    try {
      // HA-427: a practice name is the partition key for that practice's license, billing,
      // audit and content rows, so two practices sharing one name share each other's data.
      // Only the tabs that CLAIM a new practice are checked — the practitioner tab's field is
      // an invite code, and an invited teammate is meant to share their inviter's practice.
      if (tabValue === 'admin' || tabValue === 'practiceAdmin') {
        const availability = await checkPracticeNameAvailability(practiceName);

        if (!availability.available) {
          setPracticeError('That practice name is already in use. Please choose a different name.');
          setErrorMessage('That practice name is already in use. Please choose a different name.');
          setOpenErrorSnackbar(true);
          setIsLoading(false);

          return;
        }

        setPracticeError('');
      }

      await signUp(
        email,
        password,
        tabValue,
        firstName,
        lastName,
        phoneNumber,
        dateOfBirth,
        practiceName
      );
      setIsConfirm(true);
    } catch (error) {
      setErrorMessage(`Sign up failed: ${error}`);
      setOpenErrorSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // HA-232: Prevent duplicate submissions
    if (isLoading) return;
    setIsLoading(true);

    try {
      await confirmSignUp(email, confirmationCode);

      setOpenSnackbar(true);
      setIsConfirm(false);
      setIsSignUp(false);
      setIsForcePasswordChange(false);
      setAddNewUserToSystem(true);
    } catch (error) {
      setErrorMessage(`Failed to confirm account: ${error}`);
      setOpenErrorSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePracticeNameChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPracticeName(e.target.value);
    if (e.target.value.length >= 5) {
      setPracticeError('');
    }
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event | null,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleCloseErrorSnackbar = (
    event?: React.SyntheticEvent | Event | null,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenErrorSnackbar(false);
  };

  const theme = useTheme();
  const signUpPageStyles = useSignUpPageStyles(theme);

  const highlightItems = [
    'HIPAA-ready messaging, scheduling, and telehealth tools.',
    'AI assistance that keeps clients engaged and informed.',
    'Secure onboarding that gets your team productive fast.',
  ];

  // Dynamic titles based on practice branding
  const brandedPracticeName = hasCustomBranding ? displayName : 'HealioSpace';

  const heroTitle = isSignUp
    ? 'Grow a thriving wellness practice.'
    : `Welcome back to ${brandedPracticeName}.`;

  const heroSubtitle = isSignUp
    ? 'Coordinate teams, automate follow-ups, and deliver HIPAA-ready care from a single secure workspace.'
    : hasCustomBranding
      ? `Sign in to access your ${brandedPracticeName} dashboard.`
      : 'Sign in to manage your practice, support clients, and collaborate with your team in one secure place.';

  const formTitle = mfaChallengeId
    ? 'Two-factor authentication'
    : isSignUp
      ? 'Create your HealioSpace account'
      : `Sign in to ${brandedPracticeName}`;

  return (
    <Box sx={signUpPageStyles.container} data-testid="signup-page-container">
      <Box sx={signUpPageStyles.contentWrapper}>
        {/* Left Side */}
        <Box sx={signUpPageStyles.leftSide} data-testid="signup-left-side">
          <Stack spacing={3}>
            <Chip
              label={isSignUp ? 'Create your account' : 'Sign in securely'}
              color="primary"
              variant="outlined"
              sx={{ borderRadius: '999px', alignSelf: 'flex-start', fontWeight: 600 }}
            />
            <Stack spacing={1.5}>
              <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {heroTitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 420,
                }}
              >
                {heroSubtitle}
              </Typography>
            </Stack>
            <Box sx={signUpPageStyles.highlightsList}>
              {highlightItems.map((item) => (
                <Box key={item} sx={signUpPageStyles.highlightItem}>
                  <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
          <SignUpTabs tabValue={tabValue} handleTabChange={handleTabChange} />
        </Box>
        {/* Right Side */}
        <Box sx={signUpPageStyles.rightSide} data-testid="signup-right-side">
          <Box sx={signUpPageStyles.linkContainer} data-testid="signup-link-container">
            <Link component={RRLink} to="/" variant="body2" data-testid="back-to-home-link">
              Go Back to Home Page
            </Link>
          </Box>
          {/* Practice Logo - show when on branded subdomain */}
          {hasCustomBranding && (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <BrandedLogo height={50} width={200} />
            </Box>
          )}
          <Typography
            variant="h4"
            gutterBottom
            data-testid="signup-heading"
            sx={{
              fontWeight: 800,
            }}
          >
            {formTitle}
          </Typography>
          {isSignUp ? (
            tabValue === 'practitioner' ? (
              <Typography
                variant="body2"
                gutterBottom
                data-testid="practitioner-trial-text"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Begin with a {trialPeriodDays}-day trial. Unlock premium features.
              </Typography>
            ) : (
              <Typography
                variant="body2"
                gutterBottom
                data-testid="non-practitioner-trial-text"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Begin with a {trialPeriodDays}-day trial to better health.
              </Typography>
            )
          ) : (
            <Typography
              variant="body2"
              gutterBottom
              data-testid="login-supporting-text"
              sx={{
                color: 'text.secondary',
              }}
            >
              Enter your practice email and password to access your secure workspace.
            </Typography>
          )}
          {mfaChallengeId ? (
            <MfaChallengeForm
              code={mfaCode}
              isLoading={isLoading}
              isSms={mfaChallengeName === 'SMS_MFA'}
              onCancel={resetMfaChallenge}
              onCodeChange={setMfaCode}
              onSubmit={handleMfaSubmit}
            />
          ) : (
            <SignUpForm
              confirmationCode={confirmationCode}
              confirmPassword={confirmPassword}
              confirmPasswordError={confirmPasswordError}
              dateOfBirth={dateOfBirth}
              email={email}
              emailError={emailError}
              errorMessage={errorMessage}
              firstName={firstName}
              handleConfirmationCodeChange={(e) => setConfirmationCode(e.target.value)}
              handleConfirmPasswordChange={handleConfirmPasswordChange}
              handleDateOfBirthChange={handleDateOfBirthChange}
              handleEmailChange={handleEmailChange}
              handleFirstNameChange={handleFirstNameChange}
              handleLastNameChange={handleLastNameChange}
              handleOpenModal={handleOpenModal}
              handlePasswordChange={handlePasswordChange}
              handlePhoneNumberChange={handlePhoneNumberChange}
              handlePracticeNameChange={handlePracticeNameChange}
              handlePasswordReset={handleRequestReset}
              handleSubmit={isConfirm ? handleConfirm : isSignUp ? handleSignUp : handleSignIn}
              handleVerifyAndUpdatePassword={handleVerifyAndUpdatePassword}
              isConfirm={isConfirm}
              isForcePasswordChange={isForcePasswordChange}
              isLoading={isLoading}
              isPasswordReset={isPasswordReset}
              isSignUp={isSignUp}
              lastName={lastName}
              password={password}
              passwordError={passwordError}
              phoneNumber={phoneNumber}
              phoneNumberError={phoneNumberError}
              practiceError={practiceError}
              practiceName={practiceName}
              privacyPolicyChecked={privacyPolicyChecked}
              privacyPolicyClicked={privacyPolicyClicked}
              resetStep={resetStep}
              setIsPasswordReset={setIsPasswordReset}
              setIsSignUp={setIsSignUp}
              setPrivacyPolicyChecked={setPrivacyPolicyChecked}
              setPrivacyPolicyClicked={setPrivacyPolicyClicked}
              setTermsOfServiceChecked={setTermsOfServiceChecked}
              setTermsOfServiceClicked={setTermsOfServiceClicked}
              tabValue={tabValue}
              termsOfServiceChecked={termsOfServiceChecked}
              termsOfServiceClicked={termsOfServiceClicked}
            />
          )}
        </Box>
      </Box>
      <ConfirmationSnackbar
        open={openSnackbar}
        handleClose={handleCloseSnackbar}
        type={
          isForcePasswordChange
            ? 'force-password-change'
            : isPasswordReset
              ? resetStep === ResetStep.CODE
                ? 'reset-code-sent'
                : 'password-reset'
              : 'account'
        }
        data-testid="confirmation-snackbar"
      />
      <ErrorSnackbar
        open={openErrorSnackbar}
        message={errorMessage}
        handleClose={handleCloseErrorSnackbar}
        data-testid="error-snackbar"
      />
      <DocumentViewerModal
        open={isModalOpen}
        decline={handleDeclineModal}
        content={modalContent}
        title={modalTitle}
        accept={handleAcceptModal}
        data-testid="document-viewer-modal"
      />
    </Box>
  );
};

/**
 * SignUpPage wrapper that provides subdomain-based branding.
 * When accessed via {practice}.healiospace.com, shows practice branding on login.
 */
const SignUpPage: React.FC = () => {
  // Detect subdomain for practice-branded login
  const subdomain = useMemo(() => getSubdomainFromUrl(), []);

  // Wrap with BrandingProvider to provide subdomain context
  // This creates a nested provider that overrides the app-level provider
  // with subdomain-specific branding for unauthenticated users
  return (
    <BrandingProvider subdomain={subdomain}>
      <SignUpPageContent />
    </BrandingProvider>
  );
};

export default SignUpPage;
